import React from 'react';

// Erros conhecidos que podem ser ignorados (não afetam funcionalidade)
const IGNORABLE_ERRORS = [
  'removeChild',
  'insertBefore',
  'appendChild',
  'The node to be removed is not a child',
  'Failed to execute',
  'NotFoundError'
];

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, errorCode: null };
  }

  static getDerivedStateFromError(error) {
    // Verificar se é um erro que pode ser ignorado (erros de DOM em iOS Safari)
    const errorMessage = error?.message || error?.toString() || '';
    const isIgnorable = IGNORABLE_ERRORS.some(pattern => 
      errorMessage.toLowerCase().includes(pattern.toLowerCase())
    );
    
    if (isIgnorable) {
      console.warn('ErrorBoundary: Ignorando erro de DOM (comum em iOS Safari):', errorMessage);
      // Não mostrar tela de erro, apenas logar
      return null;
    }
    
    // Gerar código de erro único baseado no timestamp
    const errorCode = `ERR-${Date.now().toString(36).toUpperCase()}`;
    return { hasError: true, error, errorCode };
  }

  componentDidCatch(error, errorInfo) {
    // Verificar novamente se é ignorável
    const errorMessage = error?.message || error?.toString() || '';
    const isIgnorable = IGNORABLE_ERRORS.some(pattern => 
      errorMessage.toLowerCase().includes(pattern.toLowerCase())
    );
    
    if (isIgnorable) {
      console.warn('ErrorBoundary caught ignorable error:', errorMessage);
      // Tentar resetar o estado para permitir renderização normal
      this.setState({ hasError: false, error: null, errorInfo: null, errorCode: null });
      return;
    }
    
    this.setState({ errorInfo });
    // Log error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    console.error('Error code:', this.state.errorCode);
  }

  handleReload = () => {
    // Limpar cache e recarregar
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.reload(true);
  };

  handleGoBack = () => {
    window.history.back();
  };

  handleClearAndReload = () => {
    // Limpar localStorage relacionado ao app
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('routine_') || key.includes('report_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (e) {
      console.warn('Could not clear localStorage:', e);
    }
    window.location.reload(true);
  };

  getErrorSummary = () => {
    if (!this.state.error) return 'Erro desconhecido';
    const errorStr = this.state.error.toString();
    // Extrair apenas a primeira linha e limitar tamanho
    const firstLine = errorStr.split('\n')[0];
    return firstLine.length > 100 ? firstLine.substring(0, 100) + '...' : firstLine;
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-[#FBF4FF] to-white flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 shadow-lg max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            
            <h2 className="text-xl font-bold text-[#30244F] mb-2">
              Ops! Algo deu errado
            </h2>
            
            <p className="text-[#6B5B8C] mb-4">
              Ocorreu um erro inesperado. Por favor, tente novamente.
            </p>
            
            {/* Sempre mostrar código e resumo do erro */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-left">
              <p className="text-xs text-gray-500 mb-1">Código do erro:</p>
              <p className="text-sm font-mono font-bold text-[#8F7AC5] mb-2">{this.state.errorCode}</p>
              <p className="text-xs text-gray-500 mb-1">Detalhes:</p>
              <p className="text-xs font-mono text-red-600 break-all">{this.getErrorSummary()}</p>
            </div>
            
            <div className="flex flex-col gap-2">
              <div className="flex gap-2 justify-center">
                <button
                  onClick={this.handleGoBack}
                  className="px-4 py-2 border border-[#8F7AC5] text-[#8F7AC5] rounded-full hover:bg-[#FBF4FF] transition-colors text-sm"
                >
                  Voltar
                </button>
                <button
                  onClick={this.handleReload}
                  className="px-4 py-2 bg-[#8F7AC5] text-white rounded-full hover:bg-[#7B68B0] transition-colors text-sm"
                >
                  Recarregar
                </button>
              </div>
              <button
                onClick={this.handleClearAndReload}
                className="px-4 py-2 text-[#6B5B8C] text-xs underline hover:text-[#8F7AC5]"
              >
                Limpar dados e recarregar
              </button>
            </div>
            
            <p className="text-xs text-gray-400 mt-4">
              Se o problema persistir, tente abrir em uma guia anônima.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
